import { NavLink, useLocation } from 'react-router-dom'
import { useAdminAuth } from '../../context/AdminAuthContext'
import {
  LayoutDashboard, Package, ShoppingCart, Users,
  BarChart3, Settings, LogOut, Zap, ChevronRight,
  Wine, X, Gift, TrendingUp, Clock, Database, Mail
} from 'lucide-react'
import clsx from 'clsx'

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/invites', icon: Mail, label: 'Invites' },
  { to: '/promotions', icon: Gift, label: 'Promotions' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/forecasting', icon: TrendingUp, label: 'Forecasting' },
  { to: '/sales-history', icon: Database, label: 'Sales History' },
  { to: '/scheduler',   icon: Clock,       label: 'Scheduler'   },
  { to: '/settings',   icon: Settings,    label: 'Settings'    },
]

export default function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAdminAuth()
  const location = useLocation()

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'fixed top-0 left-0 h-full z-50 flex flex-col',
        'bg-sidebar-bg border-r border-sidebar-border',
        'transition-transform duration-300',
        'w-[260px]',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <img
              src="/logo-w.webp"
              alt="Tony's Liquor Distributor"
              className="h-9 w-auto object-contain"
              onError={e => { e.target.src = '/tonys-logo.svg'; e.target.className = 'h-9 w-9 object-contain' }}
            />
            <div>
              <div className="font-display text-xs text-gold/80 tracking-widest leading-none uppercase">Admin Panel</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-500 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <div className="px-3 pb-2">
            <span className="text-[10px] font-display font-semibold text-gray-600 uppercase tracking-widest">
              Navigation
            </span>
          </div>

          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) => clsx(
                'nav-item',
                isActive && 'active'
              )}
            >
              <Icon size={16} className="shrink-0" />
              <span className="flex-1">{label}</span>
              {location.pathname.startsWith(to) && (
                <ChevronRight size={12} className="text-primary/60" />
              )}
            </NavLink>
          ))}
        </nav>

        {/* Gold divider */}
        <div className="px-4">
          <div className="gold-line" />
        </div>

        {/* User / Logout */}
        <div className="p-4 space-y-2">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/5">
              <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center text-sm font-heading text-white">
                {user.first_name?.[0] || user.username?.[0] || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">
                  {user.first_name || user.username}
                </div>
                <div className="text-xs text-gold/70 flex items-center gap-1">
                  <Zap size={9} />
                  {user.role || 'Admin'}
                </div>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className="nav-item w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
          >
            <LogOut size={15} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  )
}
